import React, { useState, useCallback, useEffect, useMemo } from "react"
import { useAuth, APIError } from "../../../../../app/modules/auth"
import {
    WebhookRequestItem,
    WebhookSliceItem,
    WebhookSliceLead,
    WebhookSliceResult,
    createSliceCustomFolderAndFields,
    getAllWebhookRequests,
    getAllWebhookSlices,
    getLeadsMatchingWebhookSlice,
    getSliceByUniqueIdentifier
} from "../../../../../mogul/core/_requests"
import { formatLeadItemDateBetter } from "../DebugPipedrive"
import Select from 'react-select'
import usePartners from "../../../../../mogul/hooks/usePartners"
import { SyncLeadButton } from "../DebugAllLeads"
import { Link, Navigate, Route, Routes, useLocation, useParams } from "react-router-dom"
import LocationsNeedingAttentionBanner from "../../../../../mogul/components/LocationsNeedingAttentionBanner"
import useAccounts from "../../../../../mogul/hooks/useAccounts"
import { DisableLinkButton, GetAvailableLocationsGivenAServiceLocation } from "../../../../../mogul/routes/service-locations/detail/ServiceLocationSettings"
import { WebhookSliceAutomation } from "./WebhookSliceAutomation"
import { PartnerMappingRulesPage } from "../../../../../mogul/routes/PartnerMappingRulesPage"

interface AllRequestsFilterObject {
    selectedPartnerUniqueIds: string[],
    selectedPreneedOptions: string[],
    onlyUnSyncedWith360: boolean,
    showAllLeadsEvenIfLocationIsIgnored: boolean
}

const saveAllRequestsFilterToLocalStorageKey = 'webhook_requests_table_filter_v1'

interface SliceLeadItemFilterObject {
    onlyUnSyncedWith360: boolean,
}

const saveSliceLeadItemFilterToLocalStorageKey = 'webhook_slice_lead_item_filter_v1'

interface SliceFilterObject { }
const saveSliceFilterToLocalStorageKey = 'webhook_slices_table_filter_v1'

function SlicesFilterTable({
    onCancel,
    onReset,
    currentFilterObject,
    onApplyFilter,
}: {
    onCancel: () => void,
    onReset: () => void,
    currentFilterObject: SliceFilterObject | null,
    onApplyFilter: (filterObject: SliceFilterObject) => void,
}) {
    return (
        <div>
            <div className='px-7 py-5'>
                <div className='fs-5 text-dark fw-bolder'>Filter List</div>
            </div>

            <div className='separator border-gray-200'></div>

            <div className='px-7 py-5'>

                <div className='d-flex justify-content-end'>
                    <button
                        type='button'
                        className='btn btn-sm btn-light btn-active-light-primary me-2'
                        onClick={() => onReset()}
                    >
                        Reset
                    </button>
                    <button
                        type='button'
                        className='btn btn-sm btn-light btn-active-light-primary me-2'
                        onClick={() => onCancel()}
                    >
                        Cancel
                    </button>
                    <button
                        type='button'
                        className='btn btn-sm btn-primary'
                        onClick={() => onApplyFilter({
                        })}
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    )
}

function SlicesTableHeader({
    title,
    subtitle,
    currentFilterObject,
    onApplyFilter
}: {
    title: string,
    subtitle?: string,
    currentFilterObject: SliceFilterObject | null,
    onApplyFilter: (filterObject: SliceFilterObject) => void,
}) {

    const [viewingFilter, setViewingFilter] = useState(false)

    return <>
        {/* begin::Header */}
        <div className='card-header align-items-center border-0 mt-0'>
            <h3 className='card-title align-items-start flex-column'>
                <span className='fw-bolder text-dark fs-3'>
                    {title}
                </span>
                {subtitle && <span className='text-gray-400 mt-2 fw-bold fs-6'>{subtitle}</span>}
            </h3>
            <div className='card-toolbar'>
                {/* begin::Menu */}
                <button
                    type='button'
                    className='btn btn-sm btn-light me-2'
                    onClick={() => setViewingFilter(!viewingFilter)}
                >
                    <span className='indicator-label'>
                        Filter List
                    </span>
                </button>
                {/* end::Menu */}
            </div>
        </div>
        {/* end::Header */}
        {viewingFilter && <>
            <SlicesFilterTable
                onReset={() => {
                    onApplyFilter({
                        selectedPartnerUniqueIds: [],
                        selectedPreneedOptions: [],
                        onlyUnSyncedWith360: false,
                        showAllLeadsEvenIfLocationIsIgnored: false
                    })
                    localStorage.removeItem(saveAllRequestsFilterToLocalStorageKey)
                }}
                onCancel={() => setViewingFilter(false)}
                currentFilterObject={currentFilterObject}
                onApplyFilter={onApplyFilter}
            />
        </>}
    </>

}

function AllRequestsFilterWebhooksTable({
    onCancel,
    onReset,
    currentFilterObject,
    onApplyFilter,
}: {
    onCancel: () => void,
    onReset: () => void,
    currentFilterObject: AllRequestsFilterObject | null,
    onApplyFilter: (filterObject: AllRequestsFilterObject) => void,
}) {

    const { currentUser } = useAuth()

    const {
        partners
    } = usePartners(currentUser?.is_admin || false)

    const [selectedPartnerUniqueIds, setSelectedPartnerUniqueIds] = useState<string[]>(currentFilterObject ? currentFilterObject.selectedPartnerUniqueIds : [])
    const [onlyUnSyncedWith360, setOnlyUnSyncedWith360] = useState(currentFilterObject ? currentFilterObject.onlyUnSyncedWith360 : false)
    const [showAllLeadsEvenIfLocationIsIgnored, setShowAllLeadsEvenIfLocationIsIgnored] = useState(currentFilterObject ? currentFilterObject.showAllLeadsEvenIfLocationIsIgnored : false)

    return (
        <div>
            <div className='px-7 py-5'>
                <div className='fs-5 text-dark fw-bolder'>Filter List</div>
            </div>

            <div className='separator border-gray-200'></div>

            <div className='px-7 py-5'>

                <div className='mb-5'>
                    <label className='form-label fw-bold'>
                        Partner
                    </label>
                    <div>
                        <Select
                            options={[
                                { value: '', label: '-All Partners-' },
                                ...partners.map((option) => {
                                    return {
                                        value: `${option.unique_identifier}`,
                                        label: option.name
                                    }
                                })]}
                            onChange={(selectedOption) => {
                                setSelectedPartnerUniqueIds(selectedOption ? selectedOption.map((x) => x.value) : [])
                            }}
                            isMulti
                            filterOption={(option, searchValue) => {
                                const words = searchValue.toLowerCase().split(' ');
                                return words.every(word => option.label.toLowerCase().includes(word));
                            }}
                            value={partners.filter(x => (selectedPartnerUniqueIds || []).includes(`${x.unique_identifier}`)).map((option) => {
                                return {
                                    value: `${option.unique_identifier}`,
                                    label: option.name
                                }
                            })}
                        />
                    </div>
                </div>

                {false && <>

                    <div className='mb-5'>
                        <label className='form-label fw-bold'>Only those un-synced with 360:</label>
                        <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid'>
                            <input
                                className='form-check-input'
                                type='checkbox'
                                name='notifications'
                                onChange={(e) => setOnlyUnSyncedWith360(e.target.checked)}
                                checked={onlyUnSyncedWith360}
                            />
                            <label className='form-check-label'>Enabled</label>
                        </div>
                    </div>

                    <div className='mb-5'>
                        <label className='form-label fw-bold'>Show all leads, even if location has been ignored:</label>
                        <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid'>
                            <input
                                className='form-check-input'
                                type='checkbox'
                                name='notifications'
                                onChange={(e) => setShowAllLeadsEvenIfLocationIsIgnored(e.target.checked)}
                                checked={showAllLeadsEvenIfLocationIsIgnored}
                            />
                            <label className='form-check-label'>Enabled</label>
                        </div>
                    </div>

                </>}

                <div className='d-flex justify-content-end'>
                    <button
                        type='button'
                        className='btn btn-sm btn-light btn-active-light-primary me-2'
                        onClick={() => onReset()}
                    >
                        Reset
                    </button>
                    <button
                        type='button'
                        className='btn btn-sm btn-light btn-active-light-primary me-2'
                        onClick={() => onCancel()}
                    >
                        Cancel
                    </button>
                    <button
                        type='button'
                        className='btn btn-sm btn-primary'
                        onClick={() => onApplyFilter({
                            selectedPartnerUniqueIds: selectedPartnerUniqueIds,
                            selectedPreneedOptions: [],
                            onlyUnSyncedWith360: onlyUnSyncedWith360,
                            showAllLeadsEvenIfLocationIsIgnored: showAllLeadsEvenIfLocationIsIgnored
                        })}
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    )
}

function AllRequestsWebhooksTableHeader({
    title,
    subtitle,
    currentFilterObject,
    onApplyFilter
}: {
    title: string,
    subtitle?: string,
    currentFilterObject: AllRequestsFilterObject | null,
    onApplyFilter: (filterObject: AllRequestsFilterObject) => void,
}) {

    const [viewingFilter, setViewingFilter] = useState(false)

    return <>
        {/* begin::Header */}
        <div className='card-header align-items-center border-0 mt-0'>
            <h3 className='card-title align-items-start flex-column'>
                <span className='fw-bolder text-dark fs-3'>
                    {title}
                </span>
                {subtitle && <span className='text-gray-400 mt-2 fw-bold fs-6'>{subtitle}</span>}
            </h3>
            <div className='card-toolbar'>
                {/* begin::Menu */}
                <button
                    type='button'
                    className='btn btn-sm btn-light me-2'
                    onClick={() => setViewingFilter(!viewingFilter)}
                >
                    <span className='indicator-label'>
                        Filter List
                    </span>
                </button>
                {/* end::Menu */}
            </div>
        </div>
        {/* end::Header */}
        {viewingFilter && <>
            <AllRequestsFilterWebhooksTable
                onReset={() => {
                    onApplyFilter({
                        selectedPartnerUniqueIds: [],
                        selectedPreneedOptions: [],
                        onlyUnSyncedWith360: false,
                        showAllLeadsEvenIfLocationIsIgnored: false
                    })
                    localStorage.removeItem(saveAllRequestsFilterToLocalStorageKey)
                }}
                onCancel={() => setViewingFilter(false)}
                currentFilterObject={currentFilterObject}
                onApplyFilter={onApplyFilter}
            />
        </>}
    </>

}

function SliceLeadItemFilterTable({
    onCancel,
    onReset,
    currentFilterObject,
    onApplyFilter,
}: {
    onCancel: () => void,
    onReset: () => void,
    currentFilterObject: SliceLeadItemFilterObject | null,
    onApplyFilter: (filterObject: SliceLeadItemFilterObject) => void,
}) {
    const [onlyUnSyncedWith360, setOnlyUnSyncedWith360] = useState(
        currentFilterObject ? currentFilterObject.onlyUnSyncedWith360 : false
    );

    return (
        <div>
            <div className='px-7 py-5'>
                <div className='fs-5 text-dark fw-bolder'>Filter List</div>
            </div>

            <div className='separator border-gray-200'></div>

            <div className='px-7 py-5'>
                <div className='mb-5'>
                    <label className='form-label fw-bold'>Only those un-synced with 360:</label>
                    <div className='form-check form-switch form-switch-sm form-check-custom form-check-solid'>
                        <input
                            className='form-check-input'
                            type='checkbox'
                            name='onlyUnSyncedWith360'
                            onChange={(e) => setOnlyUnSyncedWith360(e.target.checked)}
                            checked={onlyUnSyncedWith360}
                        />
                        <label className='form-check-label'>Enabled</label>
                    </div>
                </div>

                <div className='d-flex justify-content-end'>
                    <button
                        type='button'
                        className='btn btn-sm btn-light btn-active-light-primary me-2'
                        onClick={() => onReset()}
                    >
                        Reset
                    </button>
                    <button
                        type='button'
                        className='btn btn-sm btn-light btn-active-light-primary me-2'
                        onClick={() => onCancel()}
                    >
                        Cancel
                    </button>
                    <button
                        type='button'
                        className='btn btn-sm btn-primary'
                        onClick={() => onApplyFilter({ onlyUnSyncedWith360 })}
                    >
                        Apply
                    </button>
                </div>
            </div>
        </div>
    );
}

function SliceLeadItemTableHeader({
    title,
    subtitle,
    currentFilterObject,
    onApplyFilter,
}: {
    title: string,
    subtitle?: string,
    currentFilterObject: SliceLeadItemFilterObject | null,
    onApplyFilter: (filterObject: SliceLeadItemFilterObject) => void,
}) {
    const [viewingFilter, setViewingFilter] = useState(false);

    return (
        <>
            {/* begin::Header */}
            <div className='card-header align-items-center border-0 mt-0'>
                <h3 className='card-title align-items-start flex-column'>
                    <span className='fw-bolder text-dark fs-3'>{title}</span>
                    {subtitle && <span className='text-gray-400 mt-2 fw-bold fs-6'>{subtitle}</span>}
                </h3>
                <div className='card-toolbar'>
                    {/* begin::Menu */}
                    <button
                        type='button'
                        className='btn btn-sm btn-light me-2'
                        onClick={() => setViewingFilter(!viewingFilter)}
                    >
                        <span className='indicator-label'>Filter List</span>
                    </button>
                    {/* end::Menu */}
                </div>
            </div>
            {/* end::Header */}
            {viewingFilter && (
                <SliceLeadItemFilterTable
                    onReset={() => {
                        onApplyFilter({ onlyUnSyncedWith360: false });
                        localStorage.removeItem(saveSliceLeadItemFilterToLocalStorageKey);
                    }}
                    onCancel={() => setViewingFilter(false)}
                    currentFilterObject={currentFilterObject}
                    onApplyFilter={onApplyFilter}
                />
            )}
        </>
    );
}

function WebhookSlicesTable() {
    const { currentUser } = useAuth();

    const isAdmin = !!currentUser?.is_admin;

    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [errorLoading, setErrorLoading] = useState<string | null>(null);

    const [results, setResults] = useState<WebhookSliceResult[]>([]);
    const [totalResults, setTotalResults] = useState(0);

    const [currentFilterObject, setCurrentFilterObject] = useState<SliceFilterObject | null>(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const loadList = useCallback(
        async (forceFilter?: SliceFilterObject, page: number = 1) => {
            setErrorLoading(null);
            if (page === 1) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            try {
                const {
                    webhook_slices: result,
                    total_webhook_slices: totalResult,
                } = await getAllWebhookSlices({
                    limit: 25, // the backend does quite a bit of computation so we limit to 25
                    page,
                    filter: forceFilter
                        ? {
                        }
                        : {
                        },
                });
                if (page === 1) {
                    setResults(result);
                } else {
                    setResults((prevResults) => [...prevResults, ...result]);
                }
                setTotalResults(totalResult);
                setHasMore(result.length === 25); // Assume there's more if we received a full page of results
            } catch (exception) {
                console.error(exception);
                const errorMessage =
                    exception instanceof APIError ? exception.message : 'Failed to load slices';
                setErrorLoading(errorMessage);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        []
    );

    useEffect(() => {
        // Load from local storage
        try {
            const savedFilter = localStorage.getItem(saveSliceFilterToLocalStorageKey);
            if (savedFilter) {
                const parsedFilter = JSON.parse(savedFilter);
                setCurrentFilterObject(parsedFilter);
                loadList(parsedFilter);
            } else {
                loadList();
            }
        } catch (error) {
            console.error('Failed to load saved filter', error);
        }
        // only load once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadMore = () => {
        if (hasMore && !loading) {
            setCurrentPage((prevPage) => prevPage + 1);
            loadList(currentFilterObject || undefined, currentPage + 1);
        }
    };

    if (loading) {
        return <div className='p-10'>
            <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
            </div>
        </div>
    }

    if (errorLoading) {
        return <div className='p-10'>
            <div className="alert alert-danger" role="alert">
                {errorLoading}
            </div>
            <div className='mt-5'>
                <button className='btn btn-primary btn-lg' onClick={() => {
                    loadList()
                }}>
                    Retry
                </button>
            </div>
        </div>
    }

    // Calculate automation stats
    const automatedSlices = results.filter(r => r.item.private_data?.linked_to_frm_location_id).length
    const automationPercentage = totalResults > 0 ? Math.round((automatedSlices / totalResults) * 100) : 0

    return <div>
        {false && <LocationsNeedingAttentionBanner inCategory="webhooks" />}

        {/* Automation Stats Card */}
        {totalResults > 0 && (
            <div className="card mt-3 mx-5">
                <div className="card-body py-3">
                    <div className="d-flex align-items-center justify-content-between">
                        <div className="d-flex align-items-center">
                            <div className="symbol symbol-50px me-3">
                                <div className="symbol-label bg-light-primary">
                                    <i className="bi bi-lightning-charge fs-2x text-primary"></i>
                                </div>
                            </div>
                            <div>
                                <div className="fs-4 fw-bold">
                                    {automatedSlices} of {totalResults} slices automated
                                </div>
                                <div className="fs-6 text-muted">
                                    Real-time sync enabled for {automationPercentage}% of slices
                                </div>
                            </div>
                        </div>
                        <div className="d-flex align-items-center">
                            <div className="progress w-150px me-3">
                                <div
                                    className="progress-bar bg-primary"
                                    style={{ width: `${automationPercentage}%` }}
                                />
                            </div>
                            <Link
                                to="/debugging/all-leads/webhooks/automation"
                                className="btn btn-sm btn-light-primary"
                            >
                                Manage Automation
                            </Link>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Header */}
        <SlicesTableHeader
            title={totalResults === 1 ? '1 Slice' : `${totalResults} Slices`}
            subtitle={!isAdmin ? '' : ''}
            currentFilterObject={currentFilterObject}
            onApplyFilter={(filterObject) => {
                setCurrentFilterObject(filterObject)
                loadList(filterObject)
                // save to local storage
                localStorage.setItem(saveSliceFilterToLocalStorageKey, JSON.stringify(filterObject))
            }}
        />
        <div className='px-10'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                {/* begin::Table head */}
                <thead>
                    <tr className='fw-bold text-muted'>
                        <th className='min-w-250px'>Name</th>
                        <th className='min-w-150px'>
                            Last Lead Received At
                        </th>
                        <th>
                            Group
                        </th>
                        <th className='min-w-100px'>
                            Automation
                        </th>
                        <th className='min-w-150px  text-end'>
                            Number of Leads
                        </th>
                    </tr>
                </thead>
                {/* end::Table head */}
                {/* begin::Table body */}
                <tbody>
                    {results.map((result: WebhookSliceResult) => {
                        return <tr key={result.item.unique_identifier}>
                            <td>
                                <Link to={`/debugging/all-leads/webhooks/slices/${result.item.unique_identifier}`}>
                                    {result.item.name}
                                </Link>
                            </td>
                            <td>
                                {formatLeadItemDateBetter(result.last_lead_received_at)}
                            </td>
                            <td>
                                {result.item.slice_group ? (
                                    <span>
                                        {result.item.slice_group.name}
                                    </span>
                                ) : (
                                    <span className='text-muted'>
                                        (no group)
                                    </span>
                                )}
                            </td>
                            <td>
                                {result.item.private_data?.linked_to_frm_location_id ? (
                                    <span className='badge badge-light-success'>
                                        <i className='bi bi-check-circle-fill me-1'></i>
                                        Automated
                                    </span>
                                ) : (
                                    <span className='badge badge-light-secondary'>
                                        <i className='bi bi-dash-circle me-1'></i>
                                        Manual
                                    </span>
                                )}
                            </td>
                            <td className='text-end'>
                                {result.results.total_count}
                            </td>
                        </tr>
                    })}
                    {!loading && hasMore && (
                        <tr>
                            <td colSpan={8}>
                                <button
                                    disabled={loadingMore}
                                    onClick={loadMore}
                                    className='btn btn-primary'
                                >
                                    {loadingMore ? 'Loading...' : 'Load More'}
                                </button>
                            </td>
                        </tr>
                    )}
                </tbody>
                {/* end::Table body */}
            </table>
        </div>
    </div>

}


function WebhooksTableAll() {
    const { currentUser } = useAuth();

    const isAdmin = !!currentUser?.is_admin;

    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [errorLoading, setErrorLoading] = useState<string | null>(null);

    const [results, setResults] = useState<WebhookRequestItem[]>([]);
    const [totalResults, setTotalResults] = useState(0);

    const [currentFilterObject, setCurrentFilterObject] = useState<AllRequestsFilterObject | null>(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    // Modal state for viewing raw data
    const [selectedRequest, setSelectedRequest] = useState<WebhookRequestItem | null>(null);
    const [showRawDataModal, setShowRawDataModal] = useState(false);
    const [activeTab, setActiveTab] = useState<'raw-body' | 'parsed-data' | 'headers'>('raw-body');

    const openRawDataModal = (item: WebhookRequestItem) => {
        setSelectedRequest(item);
        setShowRawDataModal(true);
        setActiveTab('raw-body');
    };

    const closeRawDataModal = () => {
        setSelectedRequest(null);
        setShowRawDataModal(false);
    };

    const loadList = useCallback(
        async (forceFilter?: AllRequestsFilterObject, page: number = 1) => {
            setErrorLoading(null);
            if (page === 1) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            try {
                const {
                    webhook_requests: result,
                    total_webhook_requests: totalResult,
                } = await getAllWebhookRequests({
                    limit: 25, // the backend does quite a bit of computation so we limit to 25
                    page,
                    filter: forceFilter
                        ? {
                            partner_unique_ids: forceFilter?.selectedPartnerUniqueIds || [],
                        }
                        : {
                            partner_unique_ids: currentFilterObject?.selectedPartnerUniqueIds || [],
                        },
                });
                if (page === 1) {
                    setResults(result);
                } else {
                    setResults((prevResults) => [...prevResults, ...result]);
                }
                setTotalResults(totalResult);
                setHasMore(result.length === 25); // Assume there's more if we received a full page of results
            } catch (exception) {
                console.error(exception);
                const errorMessage =
                    exception instanceof APIError ? exception.message : 'Failed to load all items';
                setErrorLoading(errorMessage);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [currentFilterObject?.selectedPartnerUniqueIds]
    );

    useEffect(() => {
        // Load from local storage
        try {
            const savedFilter = localStorage.getItem(saveAllRequestsFilterToLocalStorageKey);
            if (savedFilter) {
                const parsedFilter = JSON.parse(savedFilter);
                setCurrentFilterObject(parsedFilter);
                loadList(parsedFilter);
            } else {
                loadList();
            }
        } catch (error) {
            console.error('Failed to load saved filter', error);
        }
        // only load once.
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const loadMore = () => {
        if (hasMore && !loading) {
            setCurrentPage((prevPage) => prevPage + 1);
            loadList(currentFilterObject || undefined, currentPage + 1);
        }
    };

    if (loading) {
        return <div className='p-10'>
            <div className="spinner-border" role="status">
                <span className="sr-only">Loading...</span>
            </div>
        </div>
    }

    if (errorLoading) {
        return <div className='p-10'>
            <div className="alert alert-danger" role="alert">
                {errorLoading}
            </div>
            <div className='mt-5'>
                <button className='btn btn-primary btn-lg' onClick={() => {
                    loadList()
                }}>
                    Retry
                </button>
            </div>
        </div>
    }

    return <div>
        {false && <LocationsNeedingAttentionBanner inCategory="webhooks" />}

        {/* Raw Data Modal */}
        {showRawDataModal && selectedRequest && (
            <div className='modal fade show d-block' tabIndex={-1} style={{ backgroundColor: 'rgba(0,0,0,0.5)' }}>
                <div className='modal-dialog modal-xl modal-dialog-scrollable'>
                    <div className='modal-content'>
                        <div className='modal-header'>
                            <h5 className='modal-title'>Raw Webhook Data</h5>
                            <button
                                type='button'
                                className='btn-close'
                                onClick={closeRawDataModal}
                                aria-label='Close'
                            ></button>
                        </div>
                        <div className='modal-body'>
                            <div className='mb-4'>
                                <div className='d-flex align-items-center mb-2'>
                                    <strong className='me-2'>Request ID:</strong>
                                    <code className='text-primary'>{selectedRequest.unique_identifier}</code>
                                    <button
                                        className='btn btn-sm btn-light-primary ms-2'
                                        onClick={() => navigator.clipboard.writeText(selectedRequest.unique_identifier)}
                                        title='Copy to clipboard'
                                    >
                                        <i className='fas fa-copy'></i>
                                    </button>
                                </div>
                                <div><strong>Partner:</strong> {selectedRequest.partner.name}</div>
                                <div><strong>Created:</strong> {formatLeadItemDateBetter(selectedRequest.inserted_at)}</div>
                                <div><strong>Method:</strong> {selectedRequest.request_method}</div>
                                <div><strong>Content-Type:</strong> {selectedRequest.post_data?.content_type || 'N/A'}</div>
                                <div><strong>Path:</strong> {selectedRequest.post_data?.request_path || 'N/A'}</div>
                            </div>

                            <ul className='nav nav-tabs nav-line-tabs mb-5 fs-6'>
                                <li className='nav-item'>
                                    <a
                                        className={`nav-link ${activeTab === 'raw-body' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('raw-body')}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Raw Body
                                    </a>
                                </li>
                                <li className='nav-item'>
                                    <a
                                        className={`nav-link ${activeTab === 'parsed-data' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('parsed-data')}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Parsed Data
                                    </a>
                                </li>
                                <li className='nav-item'>
                                    <a
                                        className={`nav-link ${activeTab === 'headers' ? 'active' : ''}`}
                                        onClick={() => setActiveTab('headers')}
                                        style={{ cursor: 'pointer' }}
                                    >
                                        Headers
                                    </a>
                                </li>
                            </ul>

                            <div className='tab-content'>
                                {activeTab === 'raw-body' && (
                                    <div className='tab-pane fade show active'>
                                        <div className='bg-light p-3 rounded'>
                                            <pre style={{ maxHeight: '500px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {selectedRequest.post_data?.request_body?._raw_body ||
                                                 JSON.stringify(selectedRequest.post_data?.request_body || selectedRequest.normalized_data, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                )}
                                {activeTab === 'parsed-data' && (
                                    <div className='tab-pane fade show active'>
                                        <div className='bg-light p-3 rounded'>
                                            <pre style={{ maxHeight: '500px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {JSON.stringify(selectedRequest.normalized_data || {}, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                )}
                                {activeTab === 'headers' && (
                                    <div className='tab-pane fade show active'>
                                        <div className='bg-light p-3 rounded'>
                                            <pre style={{ maxHeight: '500px', overflow: 'auto', whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                                                {JSON.stringify(selectedRequest.post_data?.request_headers || {}, null, 2)}
                                            </pre>
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        <div className='modal-footer'>
                            <button
                                type='button'
                                className='btn btn-secondary'
                                onClick={closeRawDataModal}
                            >
                                Close
                            </button>
                        </div>
                    </div>
                </div>
            </div>
        )}

        {/* Header */}
        <AllRequestsWebhooksTableHeader
            title={totalResults === 1 ? '1 Webhook Request' : `${totalResults} Webhook Requests`}
            subtitle={!isAdmin ? '' : ''}
            currentFilterObject={currentFilterObject}
            onApplyFilter={(filterObject) => {
                setCurrentFilterObject(filterObject)
                loadList(filterObject)
                // save to local storage
                localStorage.setItem(saveAllRequestsFilterToLocalStorageKey, JSON.stringify(filterObject))
            }}
        />
        {/* Info Banner */}
        <div className='px-10 mt-3'>
            <div className='alert alert-info'>
                <div className='d-flex align-items-center'>
                    <div className='flex-grow-1'>
                        <strong>Note:</strong> Webhook requests can only be synced from a slice. Create a slice to match and sync specific leads.
                    </div>
                </div>
            </div>
        </div>
        <div className='px-10'>
            <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                {/* begin::Table head */}
                <thead>
                    <tr className='fw-bold text-muted'>
                        <th className='min-w-150px'>Created</th>
                        <th className='min-w-200px'>Request ID</th>
                        <th className='min-w-150px'>Partner</th>
                        <th className='min-w-250px' style={{ maxWidth: '300px', wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                            Data
                        </th>
                        <th className='min-w-100px text-end'>Actions</th>
                    </tr>
                </thead>
                {/* end::Table head */}
                {/* begin::Table body */}
                <tbody>
                    {results.map((item: WebhookRequestItem) => {
                        return (
                            <tr key={item.unique_identifier}>
                                <td>
                                    {formatLeadItemDateBetter(item.inserted_at)}
                                </td>
                                <td>
                                    <code className='text-primary' style={{ fontSize: '0.9rem' }}>
                                        {item.unique_identifier}
                                    </code>
                                    <button
                                        className='btn btn-sm btn-light-primary ms-2'
                                        onClick={() => {
                                            navigator.clipboard.writeText(item.unique_identifier);
                                        }}
                                        title='Copy to clipboard'
                                    >
                                        <i className='fas fa-copy'></i>
                                    </button>
                                </td>
                                <td>
                                    <Link to={`/partners/${item.partner.unique_identifier}/`}>
                                        {item.partner.name}
                                    </Link>
                                </td>
                                <td style={{ maxWidth: '300px', wordWrap: 'break-word', whiteSpace: 'pre-wrap' }}>
                                    <pre style={{ maxHeight: '100px', overflow: 'hidden', textOverflow: 'ellipsis' }}>
                                        {JSON.stringify((item.normalized_data || {}), null, 2)}
                                    </pre>
                                </td>
                                <td className='text-end'>
                                    <button
                                        className='btn btn-sm btn-light me-2'
                                        onClick={() => openRawDataModal(item)}
                                        title='View Raw Data'
                                    >
                                        <i className='fas fa-code'></i> Raw
                                    </button>
                                    {item.private_data.ghl_contact?.ghl_contact_id && (
                                        <a
                                            href={`https://app.premier360platform.com/v2/location/${item.private_data.ghl_contact.ghl_location_id}/contacts/detail/${item.private_data.ghl_contact.ghl_contact_id}`}
                                            target='_blank'
                                            rel='noreferrer'
                                            className='btn btn-sm btn-light-primary'
                                        >
                                            View in FRM
                                        </a>
                                    )}
                                </td>
                            </tr>
                        );
                    })}
                    {!loading && hasMore && (
                        <tr>
                            <td colSpan={8}>
                                <button
                                    disabled={loadingMore}
                                    onClick={loadMore}
                                    className='btn btn-primary'
                                >
                                    {loadingMore ? 'Loading...' : 'Load More'}
                                </button>
                            </td>
                        </tr>
                    )}
                </tbody>
                {/* end::Table body */}
            </table>
        </div>
    </div>

}

function WebhookSliceLeadItemTable({ slice }: { slice: WebhookSliceItem | null }) {
    const { sliceUniqueIdentifier } = useParams<{ sliceUniqueIdentifier: string }>();

    const [loading, setLoading] = useState(true);
    const [loadingMore, setLoadingMore] = useState(false);
    const [errorLoading, setErrorLoading] = useState<string | null>(null);

    const [results, setResults] = useState<WebhookSliceLead[]>([]);
    const [totalResults, setTotalResults] = useState(0);

    const [currentFilterObject, setCurrentFilterObject] = useState<SliceLeadItemFilterObject | null>(null);

    // Pagination states
    const [currentPage, setCurrentPage] = useState(1);
    const [hasMore, setHasMore] = useState(true);

    const loadList = useCallback(
        async (forceFilter?: SliceLeadItemFilterObject, page: number = 1) => {
            setErrorLoading(null);
            if (page === 1) {
                setLoading(true);
            } else {
                setLoadingMore(true);
            }
            try {
                const {
                    leads: result,
                    total_leads: totalResult,
                } = await getLeadsMatchingWebhookSlice(sliceUniqueIdentifier, {
                    limit: 25,
                    page,
                    filter: forceFilter
                        ? {
                            only_unsynced_with_360: forceFilter?.onlyUnSyncedWith360 || false,
                        }
                        : {
                            only_unsynced_with_360: currentFilterObject?.onlyUnSyncedWith360 || false,
                        },
                });
                if (page === 1) {
                    setResults(result);
                } else {
                    setResults((prevResults) => [...prevResults, ...result]);
                }
                setTotalResults(totalResult);
                setHasMore(result.length === 25);
            } catch (exception) {
                console.error(exception);
                const errorMessage =
                    exception instanceof APIError ? exception.message : 'Failed to load leads';
                setErrorLoading(errorMessage);
            } finally {
                setLoading(false);
                setLoadingMore(false);
            }
        },
        [sliceUniqueIdentifier, currentFilterObject]
    );

    useEffect(() => {
        try {
            const savedFilter = localStorage.getItem(saveSliceLeadItemFilterToLocalStorageKey);
            if (savedFilter) {
                const parsedFilter = JSON.parse(savedFilter);
                setCurrentFilterObject(parsedFilter);
                loadList(parsedFilter);
            } else {
                loadList();
            }
        } catch (error) {
            console.error('Failed to load saved filter', error);
        }
    }, [loadList]);

    const loadMore = () => {
        if (hasMore && !loading) {
            setCurrentPage((prevPage) => prevPage + 1);
            loadList(currentFilterObject || undefined, currentPage + 1);
        }
    };

    if (loading) {
        return (
            <div className='p-10'>
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    if (errorLoading) {
        return (
            <div className='p-10'>
                <div className="alert alert-danger" role="alert">
                    {errorLoading}
                </div>
                <div className='mt-5'>
                    <button className='btn btn-primary btn-lg' onClick={() => loadList()}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            {/* Automation Info Banner */}
            {slice?.private_data?.linked_to_frm_location_id && (
                <div className="alert alert-primary d-flex align-items-center mx-5 mt-5" role="alert">
                    <i className="bi bi-lightning-charge-fill fs-2 me-3"></i>
                    <div className="flex-grow-1">
                        <h4 className="alert-heading mb-1">Automation Enabled</h4>
                        <p className="mb-0">
                            New leads matching this slice are automatically synced to the 360 in real-time.
                        </p>
                    </div>
                    <Link
                        to={`/debugging/all-leads/webhooks/slices/${slice.unique_identifier}/settings`}
                        className="btn btn-sm btn-primary"
                    >
                        View Settings
                    </Link>
                </div>
            )}

            <SliceLeadItemTableHeader
                title={
                    `${slice?.name || ""} - ${totalResults === 1 ? '1 Lead' : `${totalResults} Leads`}`
                }
                currentFilterObject={currentFilterObject}
                onApplyFilter={(filterObject) => {
                    setCurrentFilterObject(filterObject);
                    loadList(filterObject);
                    localStorage.setItem(saveSliceLeadItemFilterToLocalStorageKey, JSON.stringify(filterObject));
                }}
            />
            <div className='px-10'>
                <table className='table table-row-dashed table-row-gray-300 align-middle gs-0 gy-4'>
                    <thead>
                        <tr className='fw-bold text-muted'>
                            <th className='min-w-150px'>Created</th>
                            <th className='min-w-150px'>Name</th>
                            <th className='min-w-200px'>Email</th>
                            <th className='min-w-150px'>Phone</th>
                            <th className='min-w-150px'>Interested in Preneed</th>
                            <th className='min-w-150px'></th>
                        </tr>
                    </thead>
                    <tbody>
                        {results.map((item: WebhookSliceLead) => (
                            <tr key={item.unique_identifier}>
                                <td>{formatLeadItemDateBetter(item.inserted_at)}</td>
                                <td>{item.lead_name}</td>
                                <td>{item.lead_email_address}</td>
                                <td>{item.lead_phone_number}</td>
                                <td>{item.lead_is_interested_in_preneed ? 'Yes' : 'No'}</td>
                                <td className='text-end'>
                                    {item.private_data.ghl_contact?.ghl_contact_id ? (
                                        <div>
                                            <div className='mb-2'>
                                                <a
                                                    href={`https://app.premier360platform.com/v2/location/${item.private_data.ghl_contact.ghl_location_id}/contacts/detail/${item.private_data.ghl_contact.ghl_contact_id}`}
                                                    target='_blank'
                                                    rel='noreferrer'
                                                >
                                                    View in FRM
                                                </a>
                                            </div>
                                            {slice && <SyncLeadButton uniqueIdentifier={
                                                `slice_${slice.unique_identifier}_${item.unique_identifier}`
                                            } isSecondary />}
                                        </div>
                                    ) : (
                                        <div>
                                            {slice && <SyncLeadButton uniqueIdentifier={
                                                `slice_${slice.unique_identifier}_${item.unique_identifier}`
                                            } />}
                                        </div>
                                    )}
                                </td>
                            </tr>
                        ))}
                        {!loading && hasMore && (
                            <tr>
                                <td colSpan={8}>
                                    <button
                                        disabled={loadingMore}
                                        onClick={loadMore}
                                        className='btn btn-primary'
                                    >
                                        {loadingMore ? 'Loading...' : 'Load More'}
                                    </button>
                                </td>
                            </tr>
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
}

function CreateWebhooksCustomFolderAndFields({
    linkOutToFRMWithURL,
    slice
}: {
    linkOutToFRMWithURL: string | null,
    slice: WebhookSliceItem
}) {
    const [creating, setCreating] = useState(false)
    const [errorCreating, setErrorCreating] = useState<string | null>(null)

    const handleCreateFolderAndFields = useCallback(async () => {
        try {
            setCreating(true)
            setErrorCreating(null)
            await createSliceCustomFolderAndFields({
                webhook_slice_unique_id: slice.unique_identifier
            })
            window.location.reload()
        } catch (exception: any) {
            console.error(exception)
            const errorMessage = exception instanceof APIError ? exception.message : 'Failed to create custom folder and fields for webhook slice'
            setErrorCreating(errorMessage)
        } finally {
            setCreating(false)
        }
    }, [slice.unique_identifier])

    return <div className='mt-5'>
        <div className='separator separator-dashed my-5'></div>
        <div>
            <h2>
                360 Custom Folder and Fields
            </h2>
        </div>
        {slice.private_data && <>
            {!slice.private_data.custom_folder_and_fields_have_been_created ? <div>
                {errorCreating && <div className='mt-5'>
                    <div className="alert alert-danger" role="alert">
                        {errorCreating}
                    </div>
                </div>}
                <button className='btn btn-primary btn-lg' onClick={() => handleCreateFolderAndFields()} disabled={creating}>
                    Create 360 Folder and Fields
                </button>
            </div> : <div>
                <div className="alert alert-success mb-4">
                    <div className="d-flex align-items-center">
                        <i className="bi bi-check-circle-fill fs-2 me-3"></i>
                        <div>
                            <div className="fw-bold">360 Folder and Fields Created</div>
                            <a href={`${linkOutToFRMWithURL}/v2/location/${slice.private_data.linked_to_frm_location_id}/settings/fields`} target='_blank' rel='noreferrer' className="text-success">
                                View in 360
                                <i className="bi bi-box-arrow-up-right ms-2"></i>
                            </a>
                        </div>
                    </div>
                </div>
                
                <div className="alert alert-info">
                    <div className="alert-text">
                        <strong>Need to update fields?</strong> You can recreate the custom folder and fields to add any new fields that have been added since the initial creation. This operation is safe and will not duplicate existing fields.
                    </div>
                </div>
                
                {errorCreating && (
                    <div className="alert alert-danger mb-3">
                        <div className="alert-text">{errorCreating}</div>
                    </div>
                )}
                
                <button 
                    className='btn btn-light-primary' 
                    onClick={() => handleCreateFolderAndFields()} 
                    disabled={creating}
                >
                    {creating ? 'Updating Fields...' : 'Recreate/Update Custom Fields'}
                </button>
            </div>}
        </>}
    </div>
}

interface WebhookSliceSettingsLocationState {
    justCreated?: boolean
    funeralHomeName?: string
}

function WebhookSliceSettings({ slice }: { slice: WebhookSliceItem | null }) {
    const location = useLocation()
    const locationState = location.state as WebhookSliceSettingsLocationState | null
    const { accounts } = useAccounts() // redundant retrieval but it'll make sure it's updated. May be in a global context one day.

    const [viewingAvailableLocations, setViewingAvailableLocations] = useState(false)

    const hardcodedPremierAccountUniqueId = "acct_GYtjmDNf2raFHTBX"; // this is hardcoded for now, see note below in the linkOutToFRMWithURL

    const linkOutToFRMWithURL = useMemo(() => {
        //
        // Right now service locations don't have GHL details because it's an admin detail not added to that
        // retrieval. The quickest fix right now is looking up the admin account mapping
        // and deducing the URL from that.
        //
        if (!slice) {
            return null
        }
        // hardcoding acct_GYtjmDNf2raFHTBX because we are going to assume only Premier for a while,
        // which should save us hours in needing to make this work better
        const matchingAccounts = accounts.filter(x => x.unique_identifier === hardcodedPremierAccountUniqueId);
        if (!matchingAccounts.length) {
            return null
        }
        const account = matchingAccounts[0];
        if (account.external_ghl_site_url) {
            return account.external_ghl_site_url
        }
        return null
    }, [accounts, slice])

    if (!slice) {
        return <></>
    }

    return <div className='px-10'>
        {locationState?.justCreated && (
            <div className='alert alert-success alert-dismissible fade show mt-5' role='alert'>
                <div className='alert-text'>
                    <strong>Success!</strong> Slice created for {locationState.funeralHomeName}. 
                    Now you need to link this slice to a 360 location to enable automatic lead syncing.
                </div>
                <button type='button' className='btn-close' data-bs-dismiss='alert' aria-label='Close'></button>
            </div>
        )}
        <div className='d-flex align-items-center justify-content-between mt-5 mb-5'>
            <h2 className='mb-0'>
                Settings for Slice: {slice.name}
            </h2>
            <div>
                {slice.private_data?.linked_to_frm_location_id ? (
                    <span className='badge badge-light-success fs-6 py-2 px-4'>
                        <i className='bi bi-lightning-charge-fill me-2'></i>
                        Automation Enabled
                    </span>
                ) : (
                    <span className='badge badge-light-warning fs-6 py-2 px-4'>
                        <i className='bi bi-hand-index-thumb me-2'></i>
                        Manual Sync Only
                    </span>
                )}
            </div>
        </div>
        {viewingAvailableLocations ? <div>
            <div className='mt-5'>
                <GetAvailableLocationsGivenAServiceLocation
                    serviceLocationUniqueId={slice.unique_identifier}
                    accountUniqueId={hardcodedPremierAccountUniqueId} // this is hardcoded for now, see note above
                />
            </div>
            <div className='mt-5'>
                <button className='btn btn-secondary btn-lg' onClick={() => setViewingAvailableLocations(false)}>
                    Back
                </button>
            </div>
        </div> : <div>
            {!slice.private_data?.linked_to_frm_location_id ? <div className='mt-5'>
                <button className='btn btn-primary btn-lg' onClick={() => setViewingAvailableLocations(true)}>
                    Link to FRM
                </button>
            </div> : <div>
                <div className='mt-5'>
                    Linked to FRM
                </div>
                {!linkOutToFRMWithURL ? <div>
                    FRM Location ID: {slice.private_data.linked_to_frm_location_id}
                </div> : <div>
                    {/* /v2/location/IQzMIO91nK1KTq0PCwq8/contacts/smart_list/All */}
                    FRM Location ID: <a href={`${linkOutToFRMWithURL}/v2/location/${slice.private_data.linked_to_frm_location_id}/contacts/smart_list/All`} target='_blank' rel='noreferrer'>
                        {slice.private_data?.linked_to_frm_location_id}
                    </a>
                </div>}
                <div className='mt-5'>
                    <DisableLinkButton
                        setViewingAvailableLocations={setViewingAvailableLocations}
                        serviceLocationUniqueId={slice.unique_identifier}
                        onSuccessfullyDisabled={() => {
                            window.location.reload()
                        }}
                        accountUniqueId={hardcodedPremierAccountUniqueId} // this is hardcoded for now, see note above
                    />
                </div>
                <CreateWebhooksCustomFolderAndFields
                    linkOutToFRMWithURL={linkOutToFRMWithURL}
                    slice={
                        slice
                    }
                />
            </div>}
        </div>}
    </div>
}

function WebhookSliceDetail() {
    const { pathname } = useLocation();
    const { sliceUniqueIdentifier } = useParams<{ sliceUniqueIdentifier: string }>();

    const [slice, setSlice] = useState<WebhookSliceItem | null>(null);
    const [loading, setLoading] = useState(true);
    const [errorLoading, setErrorLoading] = useState<string | null>(null);

    const fetchSlice = useCallback(async () => {
        if (!sliceUniqueIdentifier) {
            return;
        }
        setLoading(true);
        setErrorLoading(null);
        try {
            const fetchedSlice = await getSliceByUniqueIdentifier(sliceUniqueIdentifier);
            setSlice(fetchedSlice);
        } catch (exception) {
            console.error(exception);
            const errorMessage =
                exception instanceof APIError ? exception.message : 'Failed to load slice';
            setErrorLoading(errorMessage);
        } finally {
            setLoading(false);
        }
    }, [sliceUniqueIdentifier]);

    useEffect(() => {
        fetchSlice();
    }, [sliceUniqueIdentifier, fetchSlice]);

    if (!sliceUniqueIdentifier) {
        return <Navigate to='/debugging/all-leads/webhooks/slices' replace />;
    }

    if (loading) {
        return (
            <div className='p-10'>
                <div className="spinner-border" role="status">
                    <span className="sr-only">Loading...</span>
                </div>
            </div>
        );
    }

    if (errorLoading) {
        return (
            <div className='p-10'>
                <div className="alert alert-danger" role="alert">
                    {errorLoading}
                </div>
                <div className='mt-5'>
                    <button className='btn btn-primary btn-lg' onClick={fetchSlice}>
                        Retry
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div>
            <div className="d-flex justify-content-start mt-5 mx-5">
                <div className="menu menu-lg-rounded menu-column menu-lg-row menu-state-bg menu-title-gray-700 menu-state-title-primary menu-state-icon-primary menu-state-bullet-primary menu-arrow-gray-400 fw-bold my-5 my-lg-0 align-items-stretch">
                    <div className="menu-item me-lg-1">
                        <Link
                            className={[
                                "menu-link py-3 btn btn-link-primary",
                                pathname.endsWith(`/results`) ? "active" : ""
                            ].join(" ")}
                            to={`/debugging/all-leads/webhooks/slices/${sliceUniqueIdentifier}/results`}
                        >
                            <span className="menu-title">Results</span>
                        </Link>
                    </div>
                    <div className="menu-item me-lg-1">
                        <Link
                            className={[
                                "menu-link py-3 btn btn-link-primary",
                                pathname.endsWith(`/settings`) ? "active" : ""
                            ].join(" ")}
                            to={`/debugging/all-leads/webhooks/slices/${sliceUniqueIdentifier}/settings`}
                        >
                            <span className="menu-title">Settings</span>
                        </Link>
                    </div>
                </div>
            </div>
            <Routes>
                <Route
                    path="results"
                    element={<WebhookSliceLeadItemTable slice={slice} />}
                />
                <Route
                    path="settings"
                    element={<WebhookSliceSettings slice={slice} />}
                />
                <Route
                    path="*"
                    element={<Navigate to={`/debugging/all-leads/webhooks/slices/${sliceUniqueIdentifier}/results`} replace />}
                />
            </Routes>
        </div>
    );
}

export function WebhooksTable() {
    const { pathname } = useLocation()
    const { currentUser } = useAuth()
    const isDevelopment = process.env.NODE_ENV === 'development' || window.location.hostname === 'localhost'
    const isAdmin = !!currentUser?.is_admin
    
    return <div>
        <div className="d-flex justify-content-start mt-5 mx-5">
            <div className="menu menu-lg-rounded menu-column menu-lg-row menu-state-bg menu-title-gray-700 menu-state-title-primary menu-state-icon-primary menu-state-bullet-primary menu-arrow-gray-400 fw-bold my-5 my-lg-0 align-items-stretch">
                <div className="menu-item me-lg-1">
                    <Link
                        className={[
                            "menu-link py-3 btn btn-link-primary",
                            pathname.startsWith(`/debugging/all-leads/webhooks/slices`) ? "active" : ""
                        ].join(" ")}
                        to={`/debugging/all-leads/webhooks/slices`}
                    >
                        <span className="menu-title">Slices</span>
                    </Link>
                </div>
                <div className="menu-item me-lg-1">
                    <Link
                        className={[
                            "menu-link py-3 btn btn-link-primary",
                            pathname === `/debugging/all-leads/webhooks/automation` ? "active" : ""
                        ].join(" ")}
                        to={`/debugging/all-leads/webhooks/automation`}
                    >
                        <span className="menu-title">Automation</span>
                    </Link>
                </div>
                {(isDevelopment || isAdmin) && (
                    <div className="menu-item me-lg-1">
                        <Link
                            className={[
                                "menu-link py-3 btn btn-link-primary",
                                pathname === `/debugging/all-leads/webhooks/all` ? "active" : ""
                            ].join(" ")}
                            to={`/debugging/all-leads/webhooks/all`}
                        >
                            <span className="menu-title">All Requests</span>
                        </Link>
                    </div>
                )}
                {isAdmin && (
                    <div className="menu-item me-lg-1">
                        <Link
                            className={[
                                "menu-link py-3 btn btn-link-primary",
                                pathname === `/debugging/all-leads/webhooks/partner-mapping-rules` ? "active" : ""
                            ].join(" ")}
                            to={`/debugging/all-leads/webhooks/partner-mapping-rules`}
                        >
                            <span className="menu-title">Partner Rules</span>
                        </Link>
                    </div>
                )}
            </div>
        </div>
        <Routes>
            <Route path='all' element={<WebhooksTableAll />} />
            <Route path='slices' element={<WebhookSlicesTable />} />
            <Route path='slices/:sliceUniqueIdentifier/*' element={<WebhookSliceDetail />} />
            <Route path='automation' element={<WebhookSliceAutomation />} />
            <Route path='partner-mapping-rules' element={<PartnerMappingRulesPage />} />
            <Route path='*' element={<Navigate to='/debugging/all-leads/webhooks/slices' replace />} />
        </Routes>
    </div>
}